/**
 * Email parsing utilities for handling various email address formats
 * commonly found in copy-paste operations from email clients, spreadsheets, etc.
 */

export type ParsedEmail = {
    /** The extracted email address */
    email: string
    /** Optional display name (e.g., "John Doe" from "John Doe" <john@example.com>) */
    displayName?: string
    /** Whether the email address is valid */
    isValid: boolean
    /** The original input string */
    original: string
}

/**
 * Simple email validation regex
 * Supports:
 * - Standard emails: user@domain.com
 * - Plus addressing: user+tag@domain.com
 * - Subdomains: user@sub.domain.com
 * - Various TLDs including longer ones
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Validates an email address
 */
export function isValidEmail(email: string): boolean {
    if (!email || typeof email !== 'string') return false
    const trimmed = email.trim()
    return EMAIL_REGEX.test(trimmed) && trimmed.length <= 254
}

/**
 * Parses a single email string that may contain display name and angle brackets.
 *
 * Supported formats:
 * - Plain email: john@example.com
 * - Quoted name with brackets: "John Doe" <john@example.com>
 * - Unquoted name with brackets: John Doe <john@example.com>
 * - Brackets only: <john@example.com>
 */
export function parseEmailString(input: string): ParsedEmail {
    const original = input
    const trimmed = input.trim()

    if (!trimmed) {
        return { email: '', displayName: undefined, isValid: false, original }
    }

    // Pattern: anything followed by <email>
    // Captures: optional name before <, email inside <>
    const angleBracketMatch = trimmed.match(/^(.+?)\s*<([^>]+)>$/)

    if (angleBracketMatch) {
        const namePart = angleBracketMatch[1].trim()
        const emailPart = angleBracketMatch[2].trim()

        // Remove surrounding quotes from name if present
        const displayName = namePart.replace(/^["']|["']$/g, '').trim() || undefined

        return {
            email: emailPart.toLowerCase(),
            displayName,
            isValid: isValidEmail(emailPart),
            original,
        }
    }

    // Pattern: <email> only (no name)
    const bracketOnlyMatch = trimmed.match(/^<([^>]+)>$/)
    if (bracketOnlyMatch) {
        const emailPart = bracketOnlyMatch[1].trim()
        return {
            email: emailPart.toLowerCase(),
            displayName: undefined,
            isValid: isValidEmail(emailPart),
            original,
        }
    }

    // Plain email (no brackets)
    return {
        email: trimmed.toLowerCase(),
        displayName: undefined,
        isValid: isValidEmail(trimmed),
        original,
    }
}

/**
 * Splits pasted text into individual email segments, respecting quoted strings.
 * Handles separators: comma, semicolon, newline, tab
 */
function splitEmailText(text: string): string[] {
    const results: string[] = []
    let current = ''
    let inQuotes = false
    let quoteChar = ''

    for (let i = 0; i < text.length; i++) {
        const char = text[i]

        // Track quoted strings to avoid splitting inside them
        if ((char === '"' || char === "'") && !inQuotes) {
            inQuotes = true
            quoteChar = char
            current += char
        } else if (char === quoteChar && inQuotes) {
            inQuotes = false
            quoteChar = ''
            current += char
        } else if (!inQuotes && (char === ',' || char === ';' || char === '\n' || char === '\t')) {
            // Separator found outside quotes
            const trimmed = current.trim()
            if (trimmed) {
                results.push(trimmed)
            }
            current = ''
        } else {
            current += char
        }
    }

    // Don't forget the last segment
    const trimmed = current.trim()
    if (trimmed) {
        results.push(trimmed)
    }

    return results
}

/**
 * Parses multiple emails from pasted text.
 * Handles various formats and separators commonly found in email clients.
 *
 * @param text - The pasted text containing one or more email addresses
 * @returns Array of parsed emails (both valid and invalid for error reporting)
 */
export function parseEmailPaste(text: string): ParsedEmail[] {
    if (!text || typeof text !== 'string') {
        return []
    }

    const segments = splitEmailText(text)
    return segments.map(parseEmailString)
}

/**
 * Extracts only valid email addresses from parsed results, deduplicating by email.
 */
export function extractValidEmails(parsed: ParsedEmail[]): string[] {
    const seen = new Set<string>()
    const valid: string[] = []

    for (const p of parsed) {
        if (p.isValid && !seen.has(p.email)) {
            seen.add(p.email)
            valid.push(p.email)
        }
    }

    return valid
}

/**
 * Extracts invalid entries from parsed results for error reporting.
 */
export function extractInvalidEntries(parsed: ParsedEmail[]): string[] {
    return parsed.filter((p) => !p.isValid).map((p) => p.original)
}
